import { notification } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Job, SendDailySupportEmailJobData, sendDailySupportEmailJobData } from '../api/models';
import APIClient from '../api/api';
import InboundEmailList from '../components/InboundEmailList';
import { Button, Pagination, TabItem, Tabs } from '../components/ui';
import { PageBody, PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PICKS_PAGE_SIZE = 50;

const SupportJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job>();
  const [jobData, setJobData] = useState<SendDailySupportEmailJobData>();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'responses' | 'picks'>('responses');
  const [picksPage, setPicksPage] = useState(1);
  const [picksPageSize, setPicksPageSize] = useState(DEFAULT_PICKS_PAGE_SIZE);

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadJob() {
    setLoading(true);
    try {
      const resp = await APIClient.getJob(parseInt(id || ''));
      setJob(resp);
      setJobData(getJobData(resp));
    } catch (e) {
      notification.error({ message: 'Failed to load job.' });
    } finally {
      setLoading(false);
    }
  }

  function getJobData(job: Job): SendDailySupportEmailJobData | undefined {
    if (job.jobData && job.jobData.length > 0) {
      try {
        return sendDailySupportEmailJobData.parse(JSON.parse(atob(job.jobData)));
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  const allPicks = jobData?.picks ?? [];
  const totalPicks = allPicks.length;
  const paginatedPicks = allPicks.slice(
    (picksPage - 1) * picksPageSize,
    picksPage * picksPageSize,
  );

  const tabs: TabItem[] = [
    { key: 'responses', label: 'Responses', count: job?.inboundEmails.length },
    { key: 'picks', label: 'Picks', count: totalPicks || undefined },
  ];

  return (
    <>
      <PageHead
        title={jobData?.subject || `Support Job #${id}`}
        subtitle="Outbound support email job details and inbound responses."
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        }
      />
      <PageToolbar>
        <Tabs items={tabs} activeKey={activeTab} onChange={(k) => setActiveTab(k as typeof activeTab)} separator />
      </PageToolbar>

      {job && activeTab === 'responses' && (
        <PageBody padded>
          <InboundEmailList emails={job.inboundEmails} />
        </PageBody>
      )}

      {job && activeTab === 'picks' && (
        <>
          <PageBody padded>
            {totalPicks > 0 ? (
              <pre className="bg-bg-elevated border border-border rounded-md p-3 text-[12px] font-mono text-text-2 overflow-auto max-h-[500px]">
                {paginatedPicks.join('\n')}
              </pre>
            ) : (
              <div className="text-text-3 text-[12.5px] py-8 text-center">No picks for this job.</div>
            )}
          </PageBody>
          {totalPicks > picksPageSize && (
            <PageFooter
              left={
                <span>
                  Showing <b className="text-text-2">{paginatedPicks.length}</b> of{' '}
                  <b className="text-text-2">{totalPicks}</b>
                </span>
              }
              right={
                <Pagination
                  page={picksPage}
                  pageSize={picksPageSize}
                  total={totalPicks}
                  onPageChange={setPicksPage}
                  onPageSizeChange={(s) => { setPicksPageSize(s); setPicksPage(1); }}
                  loading={false}
                />
              }
            />
          )}
        </>
      )}
    </>
  );
};

export default SupportJob;
